import React, { useRef, useEffect } from 'react'
import { useFormikContext } from 'formik'
import get from 'lodash.get'
import set from 'lodash.set'
import { styled } from '@mui/material/styles'

import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import MenuItem from '@mui/material/MenuItem'
import Collapse from '@mui/material/Collapse'
import Grid from '@mui/material/Grid'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'

import AddressAutocomplete from 'components/common/AddressAutocomplete'

import {
  AGENCY_MAILING_ADDRESS_MEDIA_TYPES,
  DAYFORCE_AGENCY_PREFERENCES_ATTACH_ORDER,
  DAYFORCE_AGENCY_PREFERENCES_DOCUMENTS_SORT_BY,
  DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS,
  DAYFORCE_AGENCY_PREFERENCES_MAIL_METHODS,
} from 'utils/constants'
import useConfiguration from 'hooks/useConfiguration'



const AutoResizeWrapper = styled('div')({
  position: 'relative',
  '& > textarea': {
    resize: 'none',
    overflow: 'hidden',
  },
  '&::after': {
    content: 'attr(data-replicated-value) " "',
    whiteSpace: 'pre-wrap',
    visibility: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    padding: 'inherit',
    border: 'inherit',
    boxSizing: 'inherit',
    font: 'inherit',
    lineHeight: 'inherit',
    wordWrap: 'break-word',
  },
})

function AutoResizeTextField({ value = '', disabled = false, ...props }) {
  const wrapperRef = useRef(null)

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.setAttribute('data-replicated-value', value)
    }
  }, [value])

  return (
    <AutoResizeWrapper ref={wrapperRef}>
      <TextField
        {...props}
        multiline
        value={value}
        disabled={disabled}
        inputProps={{
          style: { resize: 'none', overflow: 'hidden' },
        }}
      />
    </AutoResizeWrapper>
  )
}const CheckboxWithLabel = ({
  label,
  name,
  disabled,
  h2 = false,
  disableGrid = false,
}) => {
  const { values, handleChange } = useFormikContext()

  if (disableGrid) {
    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Typography
          variant={h2 ? 'h2' : undefined}
          fontWeight={h2 ? 'medium' : undefined}
          my="auto"
        >
          {label}
        </Typography>
        <Checkbox
          label={label}
          value={get(values, name) || false}
          checked={get(values, name) || false}
          onChange={handleChange}
          name={name}
          size="small"
          disabled={disabled}
        />
      </Box>
    )
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          variant={h2 ? 'h2' : undefined}
          fontWeight={h2 ? 'medium' : undefined}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Checkbox
          label={label}
          value={get(values, name) || false}
          checked={get(values, name) || false}
          onChange={handleChange}
          name={name}
          size="small"
          disabled={disabled}
        />
      </Grid>
    </Grid>
  )
}

const AgencyPackageTable = ({ children }) => {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 0.5,
      }}
    >
      <Table
        size="small"
        sx={{
          '& .MuiTableCell-root': { borderBottom: 'none' },
          tableLayout: 'fixed',
        }}
      >
        <TableHead sx={{ backgroundColor: 'common.white' }}>
          <TableRow>
            <TableCell />
            <TableCell align="center" sx={{ fontWeight: 600 }}>
              Valid IDs
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>
              Applied Fors
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            '& > :nth-of-type(odd)': {
              backgroundColor: 'lightgray.light',
            },
            '& > :nth-of-type(even)': {
              backgroundColor: 'white',
            },
          }}
        >
          {children}
        </TableBody>
      </Table>
    </Box>
  )
}

const AgencyPackageRow = ({
  label,
  baseName,
  name,
  disabled,
  disabledValidIds,
  disabledAppliedFors,
}) => {
  const { values, handleChange } = useFormikContext()

  // Use individual disabled props if provided, otherwise fall back to the general disabled prop
  const validIdsDisabled =
    disabledValidIds !== undefined ? disabledValidIds : disabled
  const appliedForsDisabled =
    disabledAppliedFors !== undefined ? disabledAppliedFors : disabled

  return (
    <TableRow>
      <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
        {label}
      </TableCell>
      <TableCell align="center">
        <Checkbox
          name={`${baseName}.valid_ids.${name}`}
          disabled={validIdsDisabled}
          onChange={handleChange}
          value={get(values, `${baseName}.valid_ids.${name}`) || false}
          checked={get(values, `${baseName}.valid_ids.${name}`) || false}
        />
      </TableCell>
      <TableCell align="center">
        <Checkbox
          name={`${baseName}.applied_fors.${name}`}
          disabled={appliedForsDisabled}
          onChange={handleChange}
          value={get(values, `${baseName}.applied_fors.${name}`) || false}
          checked={get(values, `${baseName}.applied_fors.${name}`) || false}
        />
      </TableCell>
    </TableRow>
  )
}

const AgencyPackage = ({ formDisabled }) => {
  const { values, handleChange, errors } = useFormikContext()

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 4 }}>
        Agency Package
      </Typography>
      <Stack spacing={5}>
        <Stack>
          <CheckboxWithLabel
            h2
            disableGrid
            label="Return"
            name="includeAgencyPackageReturn"
            disabled={formDisabled}
          />
          <Collapse in={values?.includeAgencyPackageReturn}>
            <AgencyPackageTable>
              <AgencyPackageRow
                label="File Electronically"
                baseName="mailing_preferences.agency_package.return"
                name="file_electronically"
                disabled={formDisabled}
              />
              {Boolean(
                get(
                  values,
                  'mailing_preferences.agency_package.return.valid_ids.file_electronically'
                ) ||
                  get(
                    values,
                    'mailing_preferences.agency_package.return.applied_fors.file_electronically'
                  )
              ) && (
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 500 }}
                  >
                    <Box pl={4}>E-File Method</Box>
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      select
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="mailing_preferences.agency_package.return.valid_ids.efile_method"
                      disabled={formDisabled}
                      onChange={handleChange}
                      value={
                        get(
                          values,
                          'mailing_preferences.agency_package.return.valid_ids.efile_method'
                        ) || ''
                      }
                      error={Boolean(
                        get(
                          errors,
                          'mailing_preferences.agency_package.return.valid_ids.efile_method'
                        )
                      )}
                      helperText={get(
                        errors,
                        'mailing_preferences.agency_package.return.valid_ids.efile_method'
                      )}
                      SelectProps={{
                        displayEmpty: true,
                      }}
                      sx={{
                        '& .MuiSelect-select': {
                          color: !get(
                            values,
                            'mailing_preferences.agency_package.return.valid_ids.efile_method'
                          )
                            ? 'darkgray.light'
                            : undefined,
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ display: 'none' }}>
                        E-File Method
                      </MenuItem>
                      {Object.entries(
                        DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS
                      ).map(([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      select
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="mailing_preferences.agency_package.return.applied_fors.efile_method"
                      disabled={formDisabled}
                      onChange={handleChange}
                      value={
                        get(
                          values,
                          'mailing_preferences.agency_package.return.applied_fors.efile_method'
                        ) || ''
                      }
                      SelectProps={{
                        displayEmpty: true,
                      }}
                      sx={{
                        '& .MuiSelect-select': {
                          color: !get(
                            values,
                            'mailing_preferences.agency_package.return.applied_fors.efile_method'
                          )
                            ? 'darkgray.light'
                            : undefined,
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ display: 'none' }}>
                        E-File Method
                      </MenuItem>
                      {Object.entries(
                        DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS
                      ).map(([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
              )}
              <AgencyPackageRow
                label="File Paper"
                baseName="mailing_preferences.agency_package.return"
                name="file_paper"
                disabled={formDisabled}
              />
              <AgencyPackageRow
                label="File on CD"
                baseName="mailing_preferences.agency_package.return"
                name="file_on_cd"
                disabled={formDisabled}
              />
              <AgencyPackageRow
                label="Include Zeros"
                baseName="mailing_preferences.agency_package.return"
                name="include_zeros"
                disabled={formDisabled}
              />
              <AgencyPackageRow
                label="Include Dollars"
                baseName="mailing_preferences.agency_package.return"
                name="include_dollars"
                disabled={formDisabled}
              />
            </AgencyPackageTable>
          </Collapse>
        </Stack>
        <Stack>
          <CheckboxWithLabel
            h2
            disableGrid
            label="Wage Detail"
            name="includeAgencyPackageWageDetail"
            disabled={formDisabled}
          />
          <Collapse in={values?.includeAgencyPackageWageDetail}>
            <AgencyPackageTable>
              <AgencyPackageRow
                label="File Electronically"
                baseName="mailing_preferences.agency_package.wage_detail"
                name="file_electronically"
                disabled={formDisabled}
              />
              {Boolean(
                get(
                  values,
                  'mailing_preferences.agency_package.wage_detail.valid_ids.file_electronically'
                ) ||
                  get(
                    values,
                    'mailing_preferences.agency_package.wage_detail.applied_fors.file_electronically'
                  )
              ) && (
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 500 }}
                  >
                    <Box pl={4}>E-File Method</Box>
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      select
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="mailing_preferences.agency_package.wage_detail.valid_ids.efile_method"
                      disabled={formDisabled}
                      onChange={handleChange}
                      value={
                        get(
                          values,
                          'mailing_preferences.agency_package.wage_detail.valid_ids.efile_method'
                        ) || ''
                      }
                      error={Boolean(
                        get(
                          errors,
                          'mailing_preferences.agency_package.wage_detail.valid_ids.efile_method'
                        )
                      )}
                      helperText={get(
                        errors,
                        'mailing_preferences.agency_package.wage_detail.valid_ids.efile_method'
                      )}
                      SelectProps={{
                        displayEmpty: true,
                      }}
                      sx={{
                        '& .MuiSelect-select': {
                          color: !get(
                            values,
                            'mailing_preferences.agency_package.wage_detail.valid_ids.efile_method'
                          )
                            ? 'darkgray.light'
                            : undefined,
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ display: 'none' }}>
                        E-File Method
                      </MenuItem>
                      {Object.entries(
                        DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS
                      ).map(([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      select
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="mailing_preferences.agency_package.wage_detail.applied_fors.efile_method"
                      disabled={formDisabled}
                      onChange={handleChange}
                      value={
                        get(
                          values,
                          'mailing_preferences.agency_package.wage_detail.applied_fors.efile_method'
                        ) || ''
                      }
                      SelectProps={{
                        displayEmpty: true,
                      }}
                      sx={{
                        '& .MuiSelect-select': {
                          color: !get(
                            values,
                            'mailing_preferences.agency_package.wage_detail.applied_fors.efile_method'
                          )
                            ? 'darkgray.light'
                            : undefined,
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ display: 'none' }}>
                        E-File Method
                      </MenuItem>
                      {Object.entries(
                        DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS
                      ).map(([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
              )}
              <AgencyPackageRow
                label="File Paper"
                baseName="mailing_preferences.agency_package.wage_detail"
                name="file_paper"
                disabled={formDisabled}
              />
              <AgencyPackageRow
                label="File on CD"
                baseName="mailing_preferences.agency_package.wage_detail"
                name="file_on_cd"
                disabled={formDisabled}
              />
              <AgencyPackageRow
                label="Include Zeros"
                baseName="mailing_preferences.agency_package.wage_detail"
                name="include_zeros"
                disabled={formDisabled}
              />
              <AgencyPackageRow
                label="Include Dollars"
                baseName="mailing_preferences.agency_package.wage_detail"
                name="include_dollars"
                disabled={formDisabled}
              />
            </AgencyPackageTable>
          </Collapse>
        </Stack>
        <Stack>
          <CheckboxWithLabel
            h2
            disableGrid
            label="Payment Type(s)"
            name="includeAgencyPackagePaymentTypes"
            disabled={formDisabled}
          />
          <Collapse in={values?.includeAgencyPackagePaymentTypes}>
            <AgencyPackageTable>
              <AgencyPackageRow
                label="ACH/EFT"
                baseName="mailing_preferences.agency_package.payment_types"
                name="eft_ach"
                disabled={formDisabled}
              />
              <AgencyPackageRow
                label="Individual Check"
                baseName="mailing_preferences.agency_package.payment_types"
                name="individual_check"
                disabled={formDisabled}
                disabledValidIds={
                  formDisabled ||
                  get(
                    values,
                    'mailing_preferences.agency_package.payment_types.valid_ids.batch_check'
                  )
                }
                disabledAppliedFors={
                  formDisabled ||
                  get(
                    values,
                    'mailing_preferences.agency_package.payment_types.applied_fors.batch_check'
                  )
                }
              />
              <AgencyPackageRow
                label="Batch Check"
                baseName="mailing_preferences.agency_package.payment_types"
                name="batch_check"
                disabled={formDisabled}
                disabledValidIds={
                  formDisabled ||
                  get(
                    values,
                    'mailing_preferences.agency_package.payment_types.valid_ids.individual_check'
                  )
                }
                disabledAppliedFors={
                  formDisabled ||
                  get(
                    values,
                    'mailing_preferences.agency_package.payment_types.applied_fors.individual_check'
                  )
                }
              />
              <AgencyPackageRow
                label="Payment Voucher"
                baseName="mailing_preferences.agency_package.payment_types"
                name="payment_voucher"
                disabled={formDisabled}
              />
            </AgencyPackageTable>
          </Collapse>
        </Stack>
        <Stack>
          <CheckboxWithLabel
            h2
            disableGrid
            label="Other Requirements"
            name="includeAgencyPackageOtherRequirements"
            disabled={formDisabled}
          />
          <Collapse in={values?.includeAgencyPackageOtherRequirements}>
            <Stack
              sx={{ borderLeft: 4, borderColor: 'primary.main', ml: 1, pl: 2 }}
            >
              <CheckboxWithLabel
                label="Cover Letter"
                name="mailing_preferences.agency_package.other_requirements.cover_letter"
                disabled={formDisabled}
              />
              <CheckboxWithLabel
                label="Other Documents"
                name="mailing_preferences.agency_package.other_requirements.other_documents"
                disabled={formDisabled}
              />
              <CheckboxWithLabel
                label="Client Listing Required"
                name="mailing_preferences.agency_package.other_requirements.client_listing_required"
                disabled={formDisabled}
              />
              <CheckboxWithLabel
                label="Out of Balance Accepted"
                name="mailing_preferences.agency_package.other_requirements.out_of_balance_accepted"
                disabled={formDisabled}
              />
            </Stack>
          </Collapse>
        </Stack>
      </Stack>
    </Box>
  )
}

const AddressAutocompleteWithCollapse = ({
  disabled = false,
  show = false,
  index,
  ...props
}) => {
  const { values, handleChange, errors, setErrors, setFieldValue } =
    useFormikContext()
  const { instanceConfigurations } = useConfiguration()
  const agencyMailingAddressMediaTypes =
    instanceConfigurations?.[AGENCY_MAILING_ADDRESS_MEDIA_TYPES] || []

  const handleUpdateChange = (e) => {
    handleChange(e)

    const newErrors = { ...errors }

    set(newErrors, e.target.name, undefined)

    setErrors(newErrors)
  }

  return (
    <Stack>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: show ? 2 : 0 }}
      >
        <Typography variant="h2" fontWeight="medium">
          {props.label}
        </Typography>
        <Checkbox
          sx={{ my: 'auto' }}
          label={props.label}
          value={show || false}
          checked={show || false}
          onChange={(e) =>
            setFieldValue(`includeAddress${index + 1}`, e.target.checked)
          }
          name="show"
          size="small"
          disabled={disabled}
        />
      </Box>
      <Collapse in={show}>
        <Box
          sx={{ borderLeft: '4px solid', borderColor: 'primary.main', pl: 2 }}
        >
          <Grid container spacing={2} rowSpacing={3}>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">Media Type *</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={get(values, props.addressKeys.mediaType) || ''}
                onChange={handleChange}
                select
                name={props.addressKeys.mediaType}
                error={Boolean(get(errors, props.addressKeys.mediaType))}
                helperText={get(errors, props.addressKeys.mediaType)}
                required
                disabled={disabled}
                SelectProps={{
                  displayEmpty: true,
                }}
                sx={{
                  '& .MuiSelect-select': {
                    color: !get(values, props.addressKeys.mediaType)
                      ? 'darkgray.light'
                      : undefined,
                  },
                }}
              >
                <MenuItem value="" sx={{ display: 'none' }}>
                  Select Media Type
                </MenuItem>
                {agencyMailingAddressMediaTypes.map((mediaType) => (
                  <MenuItem key={mediaType} value={mediaType.toLowerCase()}>
                    {mediaType}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">Mail Method *</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={get(values, props.addressKeys.mailMethod) || ''}
                onChange={handleChange}
                select
                name={props.addressKeys.mailMethod}
                error={Boolean(get(errors, props.addressKeys.mailMethod))}
                helperText={get(errors, props.addressKeys.mailMethod)}
                required
                disabled={disabled}
                SelectProps={{
                  displayEmpty: true,
                }}
                sx={{
                  '& .MuiSelect-select': {
                    color: !get(values, props.addressKeys.mailMethod)
                      ? 'darkgray.light'
                      : undefined,
                  },
                }}
              >
                <MenuItem value="" sx={{ display: 'none' }}>
                  Select Mail Method
                </MenuItem>
                {Object.entries(DAYFORCE_AGENCY_PREFERENCES_MAIL_METHODS).map(
                  ([key, value]) => (
                    <MenuItem value={key} key={key}>
                      {value}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">Attach/Order *</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                value={get(values, props.addressKeys.attachOrder) || ''}
                onChange={handleChange}
                name={props.addressKeys.attachOrder}
                select
                required
                fullWidth
                size="small"
                disabled={disabled}
                error={Boolean(get(errors, props.addressKeys.attachOrder))}
                helperText={get(errors, props.addressKeys.attachOrder)}
                variant="outlined"
                SelectProps={{
                  displayEmpty: true,
                }}
                sx={{
                  '& .MuiSelect-select': {
                    color: !get(values, props.addressKeys.attachOrder)
                      ? 'darkgray.light'
                      : undefined,
                  },
                }}
              >
                <MenuItem value="" sx={{ display: 'none' }}>
                  Select Attach/Order
                </MenuItem>
                {Object.entries(DAYFORCE_AGENCY_PREFERENCES_ATTACH_ORDER).map(
                  ([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">Sort Documents</Typography>
            </Grid>
            <Grid item xs={9}>
              <Box>
                {get(values, props.addressKeys.shouldSortDocuments) ? (
                  <TextField
                    value={
                      get(values, props.addressKeys.sortDocumentsSortBy) || ''
                    }
                    onChange={(e) => {
                      if (e.target.value === 'do-not-sort') {
                        setFieldValue(
                          props.addressKeys.shouldSortDocuments,
                          false
                        )
                      } else {
                        handleChange(e)
                      }
                    }}
                    name={props.addressKeys.sortDocumentsSortBy}
                    label="Sort By"
                    select
                    fullWidth
                    size="small"
                    disabled={disabled}
                    error={Boolean(
                      get(errors, props.addressKeys.sortDocumentsSortBy)
                    )}
                    helperText={get(
                      errors,
                      props.addressKeys.sortDocumentsSortBy
                    )}
                    variant="outlined"
                  >
                    <MenuItem value="do-not-sort">
                      Do not sort documents
                    </MenuItem>
                    {Object.entries(
                      DAYFORCE_AGENCY_PREFERENCES_DOCUMENTS_SORT_BY
                    ).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Checkbox
                    name={props.addressKeys.shouldSortDocuments}
                    value={
                      get(values, props.addressKeys.shouldSortDocuments) ||
                      false
                    }
                    checked={
                      get(values, props.addressKeys.shouldSortDocuments) ||
                      false
                    }
                    onChange={(e) =>
                      setFieldValue(
                        props.addressKeys.shouldSortDocuments,
                        e.target.checked
                      )
                    }
                    disabled={disabled}
                    sx={{
                      px: 0,
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">Title</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={get(values, props.addressKeys.recipient1) || ''}
                onChange={handleChange}
                name={props.addressKeys.recipient1}
                error={Boolean(get(errors, props.addressKeys.recipient1))}
                helperText={get(errors, props.addressKeys.recipient1)}
                disabled={disabled}
                placeholder="Title"
              />
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">Department</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={get(values, props.addressKeys.recipient2) || ''}
                onChange={handleChange}
                name={props.addressKeys.recipient2}
                error={Boolean(get(errors, props.addressKeys.recipient2))}
                helperText={get(errors, props.addressKeys.recipient2)}
                placeholder="Department"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">Address Line 1 *</Typography>
            </Grid>
            <Grid item xs={9}>
              <AddressAutocomplete ignoreCountry {...props} />
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">Address Line 2</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                fullWidth
                size="small"
                placeholder="Address Line 2"
                variant="outlined"
                value={get(values, props.addressKeys.address2) || ''}
                onChange={handleUpdateChange}
                name={props.addressKeys.address2}
                error={Boolean(get(errors, props.addressKeys.address2))}
                helperText={get(errors, props.addressKeys.address2)}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">City *</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                required
                fullWidth
                size="small"
                placeholder="City"
                variant="outlined"
                value={get(values, props.addressKeys.city) || ''}
                onChange={handleUpdateChange}
                name={props.addressKeys.city}
                error={Boolean(get(errors, props.addressKeys.city))}
                helperText={get(errors, props.addressKeys.city)}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">State *</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                required
                fullWidth
                size="small"
                placeholder="State"
                variant="outlined"
                value={get(values, props.addressKeys.state) || ''}
                onChange={handleUpdateChange}
                name={props.addressKeys.state}
                error={Boolean(get(errors, props.addressKeys.state))}
                helperText={get(errors, props.addressKeys.state)}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">Zip Code *</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                required
                fullWidth
                size="small"
                placeholder="Zip Code"
                variant="outlined"
                value={get(values, props.addressKeys.zip) || ''}
                onChange={handleUpdateChange}
                name={props.addressKeys.zip}
                error={Boolean(get(errors, props.addressKeys.zip))}
                helperText={get(errors, props.addressKeys.zip)}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography my="auto">Country *</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                required
                fullWidth
                size="small"
                placeholder="Country"
                variant="outlined"
                value={get(values, props.addressKeys.country) || ''}
                onChange={handleUpdateChange}
                name={props.addressKeys.country}
                error={Boolean(get(errors, props.addressKeys.country))}
                helperText={get(errors, props.addressKeys.country)}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Stack>
  )
}

const MailingInformation = ({ formDisabled }) => {
  const { values } = useFormikContext()
  const { instanceConfigurations } = useConfiguration()
  const agencyMailingAddressMediaTypes =
    instanceConfigurations?.[AGENCY_MAILING_ADDRESS_MEDIA_TYPES] || []

  const getMediaTypeDisplay = (mediaType) => {
    return agencyMailingAddressMediaTypes.find(
      (m) => m.toLowerCase() === mediaType.toLowerCase()
    )
  }

  const getMailingAddressLabel = (index) => {
    const mediaType = get(
      values,
      `mailing_preferences.agency_package.mailing_information.address${
        index + 1
      }.media_type`
    )

    if (!mediaType) return `Mailing Address ${index + 1}`

    return `${getMediaTypeDisplay(mediaType)} Address`
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 4 }}>
        Mailing Information
      </Typography>
      <Stack spacing={5}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Stack key={index}>
            <AddressAutocompleteWithCollapse
              show={get(values, `includeAddress${index + 1}`)}
              index={index}
              label={getMailingAddressLabel(index)}
              disabled={formDisabled}
              addressKeys={{
                mediaType: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.media_type`,
                mailMethod: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.mail_method`,
                attachOrder: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.attach_order`,
                sortDocumentsSortBy: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.sort_documents_sort_by`,
                shouldSortDocuments: `shouldSortDocumentsAddress${index + 1}`,
                recipient1: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.title`,
                recipient2: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.department`,
                address: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.address_line_1`,
                address2: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.address_line_2`,
                city: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.city`,
                state: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.state`,
                zip: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.zip`,
                country: `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.country`,
              }}
              textFieldProps={{
                required: true,
                fullWidth: true,
                size: 'small',
                placeholder: 'Address Line 1',
                variant: 'outlined',
                disabled: formDisabled,
              }}
            />
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

const AdditionalRequirements = ({ formDisabled }) => {
  const { values, handleChange } = useFormikContext()

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 4 }}>
        Additional Requirements
      </Typography>
      <AutoResizeTextField
        value={values?.mailing_preferences?.additional_requirements || ''}
        onChange={handleChange}
        name="mailing_preferences.additional_requirements"
        placeholder="Enter any special requirements or notes related to this tax code"
        minRows={4}
        fullWidth
        size="small"
        variant="filled"
        disabled={formDisabled}
      />
    </Box>
  )
}

function QuarterEndReturns() {
  const { errors } = useFormikContext()
  const formDisabled = errors?.tax_code === 'Tax code already exists'

  return (
    <>
      <AgencyPackage formDisabled={formDisabled} />
      <MailingInformation formDisabled={formDisabled} />
      <AdditionalRequirements formDisabled={formDisabled} />
    </>
  )
}
export default QuarterEndReturns
